// authService.js
// Authentication service for API calls

import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  USER: "user",
};

const REMEMBER_ME_COOKIE = "ims_keep_signed_in";
const REMEMBER_ME_MAX_AGE_SEC = 60 * 60 * 24 * 30; // 30 days

function rememberMeCookieSuffix() {
  if (typeof window === "undefined") return "";
  const secure = window.location.protocol === "https:";
  return `path=/; max-age=${REMEMBER_ME_MAX_AGE_SEC}; samesite=lax${secure ? "; secure" : ""}`;
}

/** @returns {boolean} */
export function getRememberMeCookie() {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${REMEMBER_ME_COOKIE}=1`));
}

/** Persist "keep me signed in" preference in a cookie (30 days). */
export function setRememberMeCookie(enabled) {
  if (typeof document === "undefined") return;
  if (enabled) {
    document.cookie = `${REMEMBER_ME_COOKIE}=1; ${rememberMeCookieSuffix()}`;
  } else {
    document.cookie = `${REMEMBER_ME_COOKIE}=; path=/; max-age=0; samesite=lax`;
  }
}

/**
 * Remove tenantLogo (and other large fields) from user so we don't exceed localStorage quota.
 * Logo is fetched from API when needed for display.
 */
export function userForStorage(user) {
  if (!user) return user;
  const { tenantLogo, ...rest } = user;
  return rest;
}

/**
 * Store authentication data in localStorage.
 * User is stored without tenantLogo to avoid QuotaExceededError (logos can be large base64).
 */
function storeAuthData(accessToken, refreshToken, user) {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userForStorage(user)));
}

/**
 * Clear authentication data from localStorage
 */
function clearAuthData() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  setRememberMeCookie(false);
}

/**
 * Get stored access token
 */
export function getAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken() {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Get stored user data
 */
export function getStoredUser() {
  const userStr = localStorage.getItem(STORAGE_KEYS.USER);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error("Error parsing stored user data:", error);
    return null;
  }
}

/** True if JWT is missing or past expiry (60s skew buffer). */
export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000 - 60_000;
  } catch {
    return true;
  }
}

/**
 * Check if user has a restorable session (valid access token or refresh token).
 */
export function isAuthenticated() {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (accessToken && !isTokenExpired(accessToken)) return true;
  return !!refreshToken;
}

/**
 * Restore session on app load: refresh access token when expired but refresh token is still valid.
 * @returns {Promise<{ success: boolean, user?: object }>}
 */
export async function restoreSession() {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const storedUser = getStoredUser();

  if (!accessToken && !refreshToken) {
    return { success: false };
  }

  if ((!accessToken || isTokenExpired(accessToken)) && refreshToken) {
    const refreshResult = await refreshAccessToken();
    if (!refreshResult.success) {
      return { success: false };
    }
  } else if (!accessToken || isTokenExpired(accessToken)) {
    clearAuthData();
    return { success: false };
  }

  const user = storedUser || getStoredUser();
  if (!user) {
    clearAuthData();
    return { success: false };
  }

  return { success: true, user };
}

/**
 * Login with email and password
 */
export async function login(email, password, rememberMe = false, organizationCode = "") {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      email,
      password,
      rememberMe: !!rememberMe,
      organizationCode: organizationCode ? organizationCode.trim() : undefined,
    });

    if (response.data.success) {
      const data = response.data.data || {};
      if (data.accessToken && data.user) {
        storeAuthData(data.accessToken, data.refreshToken, data.user);
        setRememberMeCookie(!!rememberMe);
        return { success: true, user: data.user };
      }
      return {
        success: true,
        message: response.data.message,
        requiresOTP: true,
        email: data.email,
      };
    } else {
      return {
        success: false,
        error: response.data.error || "Login failed",
        message: response.data.message,
        locked: response.data.error === "Account Locked",
        rateLimited: response.data.error === "Too Many Requests",
        data: response.data.data || null,
      };
    }
  } catch (error) {
    console.error("Login error:", error);
    const data = error.response?.data || {};
    return {
      success: false,
      error: data.error || "Network Error",
      message: data.message || "An error occurred during login",
      locked: data.error === "Account Locked",
      rateLimited: data.error === "Too Many Requests",
      data: data.data || null,
    };
  }
}

/**
 * Ask the backend whether login is currently restricted for this client IP
 * and optional email / organization code.
 */
export async function getLoginRestriction(email = "", organizationCode = "") {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login-restriction`, {
      email: email ? email.trim() : undefined,
      organizationCode: organizationCode ? organizationCode.trim() : undefined,
    });
    if (response.data?.success) {
      return { success: true, data: response.data.data || { restricted: false } };
    }
    return { success: false, data: { restricted: false } };
  } catch (error) {
    console.error("Login restriction check error:", error);
    return { success: false, data: { restricted: false } };
  }
}

export async function verifyLoginOTP(email, otp, rememberMe = false) {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/verify-login-otp`, {
      email,
      otp,
      rememberMe: !!rememberMe,
    });

    if (response.data.success) {
      const { user, accessToken, refreshToken } = response.data.data;

      // store tokens (VERY IMPORTANT)
      storeAuthData(accessToken, refreshToken, user);
      setRememberMeCookie(!!rememberMe);

      return { success: true, user };
    }

    return {
      success: false,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "OTP verification failed",
    };
  }
}

/**
 * Logout user
 */
export async function logout() {
  try {
    const token = getAccessToken();
    if (token) {
      // Call logout endpoint (optional - mainly for server-side cleanup)
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    }
  } catch (error) {
    console.error("Logout error:", error);
    // Continue with local cleanup even if API call fails
  } finally {
    clearAuthData();
  }
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken() {
  try {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken,
    });

    if (response.data.success) {
      const { accessToken } = response.data.data;
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      return { success: true, accessToken };
    } else {
      throw new Error(response.data.message || "Token refresh failed");
    }
  } catch (error) {
    console.error("Token refresh error:", error);
    // Clear auth data on refresh failure
    clearAuthData();
    return {
      success: false,
      error: error.response?.data?.error || "Token Refresh Failed",
      message:
        error.response?.data?.message || "Unable to refresh authentication",
    };
  }
}

/**
 * Get current user profile from API
 */
export async function getProfile(retried = false) {
  try {
    let token = getAccessToken();

    if (!token || isTokenExpired(token)) {
      const refreshResult = await refreshAccessToken();
      if (!refreshResult.success) {
        return {
          success: false,
          error: "Not Authenticated",
          message: "No authentication token found",
        };
      }
      token = refreshResult.accessToken;
    }

    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.data.success) {
      const { user } = response.data.data;
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userForStorage(user)));
      return { success: true, user };
    }

    return {
      success: false,
      error: response.data.error || "Failed to get profile",
      message: response.data.message,
    };
  } catch (error) {
    console.error("Get profile error:", error);

    if (error.response?.status === 401 && !retried) {
      const refreshResult = await refreshAccessToken();
      if (refreshResult.success) {
        return getProfile(true);
      }
    }

    return {
      success: false,
      error: "Network Error",
      message: error.response?.data?.message || "Unable to fetch profile",
    };
  }
}

/**
 * Request password reset OTP (works for all users including super_admin)
 */
export async function requestPasswordResetOTP(email, organizationCode = "") {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/request-reset`, {
      email,
      organizationCode: organizationCode ? organizationCode.trim() : undefined,
    });

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Request password reset OTP error:", error);
    return {
      success: false,
      error: "Request Failed",
      message:
        error.response?.data?.message || "Unable to request password reset",
    };
  }
}

/**
 * Verify OTP code (works for all users including super_admin)
 */
export async function verifyOTP(email, otp) {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/verify-otp`, {
      email,
      otp,
    });

    return {
      success: true,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (error) {
    console.error("Verify OTP error:", error);
    return {
      success: false,
      error: "Verification Failed",
      message: error.response?.data?.message || "Unable to verify OTP",
    };
  }
}

/**
 * Reset password using verified OTP (works for all users including super_admin)
 */
export async function resetPasswordWithOTP(email, otp, newPassword) {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
      email,
      otp,
      newPassword,
    });

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Reset password with OTP error:", error);
    return {
      success: false,
      error: "Reset Failed",
      message: error.response?.data?.message || "Unable to reset password",
      details: error.response?.data?.details,
    };
  }
}

/**
 * Request password reset (legacy - kept for backward compatibility)
 */
export async function requestPasswordReset(email, organizationCode = "") {
  return requestPasswordResetOTP(email, organizationCode);
}

/**
 * Reset password using reset token (legacy - kept for backward compatibility)
 */
export async function resetPassword(token, newPassword) {
  // This is the old token-based method, but we now use OTP
  // For backward compatibility, we'll try to use it as OTP if it's 6 digits
  if (token && token.length === 6 && /^\d+$/.test(token)) {
    // Assume it's an OTP and email is needed
    console.warn("resetPassword called with what appears to be an OTP. Use resetPasswordWithOTP instead.");
  }
  return {
    success: false,
    error: "Deprecated",
    message: "Please use the OTP-based password reset flow",
  };
}

/**
 * Request OTP for change password (for logged-in users)
 */
export async function requestChangePasswordOTP() {
  try {
    const token = getAccessToken();

    const response = await axios.post(
      `${API_BASE_URL}/auth/request-change-password-otp`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send OTP",
    };
  }
}

/**
 * Change password for authenticated users (works for all authenticated users including super_admin)
 */
export async function changePassword(currentPassword, newPassword, otp) {
  try {
    const token = getAccessToken();

    if (!token) {
      return {
        success: false,
        error: "Not Authenticated",
        message: "No authentication token found",
      };
    }

    const response = await axios.post(
      `${API_BASE_URL}/auth/change-password`,
      {
        currentPassword,
        newPassword,
        otp,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Change password error:", error);
    return {
      success: false,
      error: "Change Failed",
      message: error.response?.data?.message || "Unable to change password",
      details: error.response?.data?.details,
    };
  }
}

/**
 * Update company logo for the authenticated user's tenant (admin only).
 * @param {string} logoDataUrl - data:image/... base64 URL
 */
export async function updateTenantLogo(logoDataUrl) {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return { success: false, message: "Not authenticated" };
    }
    const response = await axios.put(
      `${API_BASE_URL}/auth/tenant-logo`,
      { logo: logoDataUrl },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.data.success) {
      const profile = await getProfile();
      return {
        success: true,
        message: response.data.message,
        tenantLogo: response.data.data?.tenantLogo,
        user: profile.success ? profile.user : undefined,
      };
    }
    return {
      success: false,
      message: response.data.message || "Failed to update logo",
    };
  } catch (error) {
    console.error("Update tenant logo error:", error);
    return {
      success: false,
      message:
        error.response?.data?.message || "Unable to update company logo",
    };
  }
}

export default {
  login,
  logout,
  refreshAccessToken,
  getProfile,
  updateTenantLogo,
  requestPasswordReset,
  requestPasswordResetOTP,
  verifyOTP,
  resetPassword,
  resetPasswordWithOTP,
  changePassword,
  isAuthenticated,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  verifyLoginOTP,
  requestChangePasswordOTP,
  restoreSession,
  isTokenExpired,
};
